<template>
  <div class="peiso-modal peiso-bind">
    <!-- Loading State -->
    <div v-if="isLoading && locationList.length === 0" class="loading-container w-100 d-flex align-items-center justify-content-center">
      <div class="loading-label-wrapper">
        <i class="el-icon-loading loading"></i>
        <p>Loading Oolio organizations...</p>
      </div>
    </div>

    <div v-else class="body-container">
      <div class="field-group">
          <div>
                <div class="header-container m-top-1rem m-bottom-1rem">
                  <span>Select Organization</span>
              </div>
              <el-select
                filterable
                v-model="selectedLocation"
                @change="onSelectLocation"
                placeholder="Select an organization"
                class="peiso-select-default w-100 m-bottom-1rem"
                :disabled="isLoading"
                aria-label="Select organization">
                  <el-option
                    v-for="(item, i) in locationList"
                    :key="`site-${i}`"
                    :label="item.name"
                    :value="item.id">
                  </el-option>
              </el-select>
          </div>
          <template v-if="step == 1">
              <div class="header-container m-top-1rem m-bottom-1rem">
                  <span>Select Business</span>
              </div>
              <p class="m-bottom-1rem text-muted">
                Choose one business to configure
              </p>
              <span v-if="validationMessage" class="errortext m-bottom-1rem" role="alert">{{ validationMessage }}</span>
              <div class="list-field-group">
                  <el-checkbox-group
                    :value="selectedBusiness"
                    class="peiso-checkbox"
                    aria-label="Business selection">
                      <el-checkbox
                        v-for="(item, i) in businessList"
                        @change="handleSelectedBusiness(item)"
                        :key="`checkbox-business-${i}`"
                        :label="item">
                        {{ item.name }}
                      </el-checkbox>
                  </el-checkbox-group>
              </div>
          </template>
          <template v-if="step == 2">
              <div class="header-container m-top-1rem m-bottom-1rem">
                  <span>Select Locations</span>
                  <span v-if="selectedBusiness.length > 0" class="text-muted" style="font-size: 12px; margin-left: 8px;">
                    ({{ filteredLocations.length }} {{ filteredLocations.length === 1 ? 'location' : 'locations' }} available)
                  </span>
              </div>
              <span v-if="validationMessage" class="errortext m-bottom-1rem" role="alert">{{ validationMessage }}</span>
              <div class="list-field-group" :class="{'d-flex align-items-center justify-content-center' : !filteredLocations.length}">
                  <template v-if="filteredLocations.length">
                      <div class="peiso-checkbox terminals checkbox-select-all mb-1" v-if="isPro">
                          <el-checkbox
                            label="all"
                            :disabled="!isPro"
                            @change="checkedAllTerminal"
                            :key="`checkbox-terminal-1`"
                            v-model="isCheckedTerminal"
                            aria-label="Select all terminals">
                            {{ isCheckedTerminal ? 'Unselect All' : 'Select All' }}
                          </el-checkbox>
                      </div>
                      <el-checkbox-group
                        v-model="selectedTerminals"
                        class="peiso-checkbox terminals"
                        @change="handleSelectedTerminals"
                        aria-label="Terminal selection">
                          <el-checkbox
                            v-for="(item, i) in filteredLocations"
                            :key="`checkbox-${i}`"
                            :label="item">
                            {{ item.name }}
                          </el-checkbox>
                      </el-checkbox-group>
                  </template>
                  <template v-else>
                      <div class="p-2">
                        <i class="el-icon-info" style="font-size: 24px; margin-bottom: 10px;"></i>
                        <p>No locations available from the selected business.</p>
                        <p style="font-size: 12px; margin-top: 10px;">Click 'Next' to continue with category selection.</p>
                      </div>
                  </template>
              </div>
              <span v-if="filteredLocations.length && !selectedTerminals.length" class="errortext m-top-1rem" role="alert">
                ⓘ If no location is selected, orders will not be filtered by location
              </span>
          </template>

          <template v-if="isPro && step == 3">
            <template v-if="categories.length">
                <div class="header-container m-top-1rem m-bottom-1rem">
                    <span>Select Revenue Group</span>
                </div>
                <p class="m-bottom-1rem text-muted">
                  Choose which revenue categories to track
                </p>
                <span v-if="validationMessage" class="errortext m-bottom-1rem" role="alert">{{ validationMessage }}</span>
                <div class="list-field-group">
                    <el-checkbox-group
                      :value="checkedRevenueGroup"
                      class="peiso-checkbox"
                      aria-label="Revenue group selection">
                        <el-checkbox
                          v-for="(item, i) in revenues"
                          @change="handleSelectedRevenue(item)"
                          :key="`checkbox-revenue-${i}`"
                          :label="item">
                          {{ item.charAt(0).toUpperCase() + item.slice(1) }}
                        </el-checkbox>
                    </el-checkbox-group>
                </div>
            </template>
        </template>


        <template v-if="isPro && step == 4">
          <template v-if="categories.length">
              <div class="header-container m-top-1rem m-bottom-1rem">
                  <span>Select Food Categories</span>
              </div>
              <p class="m-bottom-1rem text-muted">
                Choose which product categories should be tracked as food revenue
              </p>
              <span v-if="validationMessage" class="errortext m-bottom-1rem" role="alert">{{ validationMessage }}</span>
              <div class="list-field-group">
                  <div class="peiso-checkbox checkbox-select-all mb-1" v-if="isPro">
                      <el-checkbox
                        label="all"
                        :disabled="!isPro"
                        @change="checkedAllFood"
                        :key="`checkbox-beverage-102`"
                        v-model="isCheckedFood"
                        aria-label="Select all food categories">
                        {{ isCheckedFood ? 'Unselect All' : 'Select All' }}
                      </el-checkbox>
                  </div>
                  <el-checkbox-group
                    v-model="checkedFood"
                    class="peiso-checkbox"
                    @change="handleSelectedFood"
                    aria-label="Food category selection">
                      <el-checkbox
                        v-for="(item) in categories"
                        :key="`checkbox-food-${item.id}`"
                        :disabled="!isPro"
                        :label="item">
                        {{ item.name }}
                      </el-checkbox>
                  </el-checkbox-group>
              </div>
          </template>
        </template>

        <template v-if="isPro && step == 5">
            <template v-if="categories.length">
              <div class="header-container m-top-1rem m-bottom-1rem">
                  <span>Select Beverage Categories</span>
              </div>
              <p class="m-bottom-1rem text-muted">
                Choose which product categories should be tracked as beverage revenue
              </p>
              <span v-if="validationMessage" class="errortext m-bottom-1rem" role="alert">{{ validationMessage }}</span>
              <div class="list-field-group">
                  <div class="peiso-checkbox checkbox-select-all mb-1" v-if="isPro">
                      <el-checkbox
                        label="all"
                        :key="`checkbox-beverage-101`"
                        :disabled="!isPro"
                        @change="checkedAllBeverage"
                        v-model="isCheckedBeverage"
                        aria-label="Select all beverage categories">
                        {{ isCheckedBeverage ? 'Unselect All' : 'Select All' }}
                      </el-checkbox>
                  </div>
                  <el-checkbox-group
                    v-model="checkedBeverage"
                    class="peiso-checkbox"
                    @change="handleSelectedBeverage"
                    aria-label="Beverage category selection">
                      <el-checkbox
                        v-for="(item) in categories"
                        :key="`checkbox-beverage-${item.id}`"
                        :disabled="!isPro"
                        :label="item">
                        {{ item.name }}
                      </el-checkbox>
                  </el-checkbox-group>
              </div>
          </template>
        </template>

        <template>
          <div class="d-flex gap-10 m-top-1rem" v-if="isPro && !isComplete">
              <el-button
                :disabled="isLoading || step === 0"
                type="peiso-default"
                class="w-100"
                @click="back"
                aria-label="Go back to previous step">
                Back
              </el-button>

              <el-button
                type="peiso-primary-pink"
                class="w-100"
                @click="next(step)"
                :disabled="!selectedLocation || isLoading"
                aria-label="Continue to next step">
                {{ isLoading ? 'Loading...' : 'Next' }}
              </el-button>
          </div>
          <div v-else class="d-flex gap-10 m-top-1rem">
              <el-button
                type="peiso-default"
                class="w-100"
                :disabled="isLoading"
                @click="back"
                aria-label="Go back to previous step">
                Back
              </el-button>
              <el-button
                type="peiso-primary-pink"
                class="w-100"
                :disabled="isLoading"
                @click="confirmCompleteOolio"
                aria-label="Complete Oolio configuration">
                <i v-if="isLoading" class="el-icon-loading"></i>
                {{ isLoading ? 'Completing...' : 'Complete' }}
              </el-button>
          </div>
      </template>
      </div>
      </div>

  </div>
</template>

<script>
import api from '../../api/api';
import utils from '../../utils/utils';

// Constants
const STEPS = {
  SELECT_ORGANIZATION: 0,
  SELECT_BUSINESSES: 1,
  SELECT_TERMINALS: 2,
  SELECT_REVENUE_GROUP: 3,
  SELECT_FOODS: 4,
  SELECT_BEVERAGES: 5
};

const REVENUE_GROUPS = {
  BOTH: 'both',
  FOOD: 'food',
  BEVERAGE: 'beverage'
};

export default {
  name: 'OolioConfiguration',
  props: {
    code: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      // Step Management
      step: STEPS.SELECT_ORGANIZATION,
      isComplete: false,

      // Loading States
      isLoading: false,

      // Data Lists
      locationList: [],
      businessList: [],
      categories: [],
      devices: [],

      // User Selections
      selectedLocation: null,
      selectedBusiness: [],
      selectedTerminals: [],
      checkedFood: [],
      checkedBeverage: [],

      // Checkbox States
      isCheckedTerminal: false,
      isCheckedFood: false,
      isCheckedBeverage: false,

      // Revenue Group
      checkedRevenueGroup: REVENUE_GROUPS.BOTH,
      revenueGroupId: REVENUE_GROUPS.BOTH,
      revenues: Object.values(REVENUE_GROUPS),

      // Validation
      validationMessage: '',

      // Feature Flags
      isPro: true
    }
  },
  computed: {
    /**
     * Returns locations filtered by selected business
     * @returns {Array} Filtered locations based on business selection
     */
    filteredLocations() {
      // If no business selected, return all locations
      if (this.selectedBusiness.length === 0) {
        return this.devices;
      }

      // Filter locations by selected business ID (first and only item in array)
      const businessId = this.selectedBusiness[0].id;
      return this.devices.filter(location =>
        location.businessId === businessId
      );
    }
  },
  watch: {
    /**
     * Determine if wizard is complete based on current step and revenue group selection
     */
    step() {
      const isFoodComplete = this.step === STEPS.SELECT_FOODS && this.revenueGroupId === REVENUE_GROUPS.FOOD;
      const isBeverageComplete = this.step === STEPS.SELECT_BEVERAGES && this.revenueGroupId === REVENUE_GROUPS.BEVERAGE;
      const isBothComplete = this.step === STEPS.SELECT_BEVERAGES && this.revenueGroupId === REVENUE_GROUPS.BOTH;

      this.isComplete = isFoodComplete || isBeverageComplete || isBothComplete;
    },
    /**
     * Clear terminal selections when business changes
     */
    selectedBusiness() {
      this.selectedTerminals = [];
      this.isCheckedTerminal = false;
    }
  },
  methods: {
    /**
     * Validates final configuration selections
     * @returns {string|null} Error message or null if valid
     */
    validateFinalConfiguration() {
      if (!this.revenueGroupId) {
        return 'Please select revenue group';
      }

      const needsFood = this.revenueGroupId === REVENUE_GROUPS.FOOD || this.revenueGroupId === REVENUE_GROUPS.BOTH;
      const needsBeverage = this.revenueGroupId === REVENUE_GROUPS.BEVERAGE || this.revenueGroupId === REVENUE_GROUPS.BOTH;

      if (needsFood && this.checkedFood.length === 0) {
        return 'Please select foods';
      }

      if (needsBeverage && this.checkedBeverage.length === 0) {
        return 'Please select beverages';
      }

      return null;
    },

    /**
     * Builds the binding data payload
     * @returns {Object} Configuration data for API
     */
    buildBindingData() {
      const data = {
        organizationId: this.selectedLocation,
        revenueGroup: this.revenueGroupId,
        locations: this.selectedTerminals.length > 0 ? this.selectedTerminals.map(item => item.id) : [],
        foods: [],
        beverages: []
      };

      const needsFood = this.revenueGroupId === REVENUE_GROUPS.FOOD || this.revenueGroupId === REVENUE_GROUPS.BOTH;
      const needsBeverage = this.revenueGroupId === REVENUE_GROUPS.BEVERAGE || this.revenueGroupId === REVENUE_GROUPS.BOTH;

      if (needsFood) {
        data.foods = this.checkedFood.map(item => item.id);
      }

      if (needsBeverage) {
        data.beverages = this.checkedBeverage.map(item => item.id);
      }

      return data;
    },

    /**
     * Completes the Oolio configuration and sends binding data to API
     */
    async confirmCompleteOolio() {
      this.isLoading = true;
      this.validationMessage = '';

      const validationError = this.validateFinalConfiguration();
      if (validationError) {
        this.validationMessage = validationError;
        this.isLoading = false;
        return;
      }

      const data = this.buildBindingData();

      console.log(data);

      try {
        const res = await api.oolioCompleteBinding(data);
        this.isLoading = false;

        if (res.data.code === 0) {
          utils.alert2('', 'Successfully connected to Oolio.', 'success');
          this.$emit('oolio-bind', data);
        } else {
          utils.alert2('', 'Error Oolio binding configuration', 'error');
        }
      } catch (err) {
        this.isLoading = false;

        if (err && err.response && err.response.data && err.response.data.message) {
          utils.alert2('', err.response.data.message, 'error');
        } else {
          utils.alert2('', 'An unexpected error occurred', 'error');
        }
      }
    },
    /**
     * Handles business selection (radio button behavior - only one at a time)
     * @param {Object} item - Selected business item
     */
    handleSelectedBusiness(item) {
      this.selectedBusiness = [item];
      // Clear terminal selection when business changes
      this.selectedTerminals = [];
      this.isCheckedTerminal = false;
    },

    /**
     * Handles terminal selection changes
     * @param {Array} selectedItems - Array of selected terminal items
     */
    handleSelectedTerminals(selectedItems) {
      this.selectedTerminals = selectedItems;
      this.isCheckedTerminal = false;
    },

    /**
     * Toggles all terminals selection
     */
    checkedAllTerminal() {
      this.selectedTerminals = this.isCheckedTerminal ? this.filteredLocations : [];
    },

    /**
     * Toggles all food categories selection
     */
    checkedAllFood() {
      this.checkedFood = this.isCheckedFood ? this.categories : [];
    },

    /**
     * Toggles all beverage categories selection
     */
    checkedAllBeverage() {
      this.checkedBeverage = this.isCheckedBeverage ? this.categories : [];
    },

    /**
     * Handles food category selection changes
     * @param {Array} selectedItems - Array of selected food categories
     */
    handleSelectedFood(selectedItems) {
      this.checkedFood = selectedItems;
      this.isCheckedFood = false;
    },

    /**
     * Handles revenue group selection
     * @param {string} item - Selected revenue group ('food', 'beverage', or 'both')
     */
    handleSelectedRevenue(item) {
      this.checkedRevenueGroup = [item];
      this.revenueGroupId = item;
    },

    /**
     * Handles beverage category selection changes
     * @param {Array} selectedItems - Array of selected beverage categories
     */
    handleSelectedBeverage(selectedItems) {
      this.checkedBeverage = selectedItems;
      this.isCheckedBeverage = false;
    },
    /**
     * Navigate to previous step in the wizard
     * Handles special cases for revenue group-based navigation
     */
    async back() {
      this.validationMessage = '';

      // Special navigation for beverage-only path
      if (this.step === STEPS.SELECT_BEVERAGES && this.revenueGroupId === REVENUE_GROUPS.BEVERAGE) {
        this.step = STEPS.SELECT_REVENUE_GROUP;
      }
      // Skip terminals if none available (go back to business selection)
      else if (this.step === STEPS.SELECT_REVENUE_GROUP && this.filteredLocations.length === 0) {
        this.step = STEPS.SELECT_TERMINALS;
      }
      // Normal backwards navigation
      else {
        this.step = this.step - 1;
      }

      // Cancel configuration if going back from first step
      if (this.step < 0) {
        await api.oolioUnbinding();
        this.$emit('oolio-bind', null);
      }
    },

    /**
     * Navigate to next step in the wizard
     * Validates current step before proceeding
     * @param {number} step - Current step number
     */
    next(step) {
      this.validationMessage = '';

      // Step 0: Organization Selection
      if (step === STEPS.SELECT_ORGANIZATION) {
        if (this.businessList.length > 0) {
          this.step = STEPS.SELECT_BUSINESSES;
        } else if (this.categories.length > 0) {
          this.step = STEPS.SELECT_REVENUE_GROUP;
        } else {
          this.validationMessage = 'Error fetching business data';
        }
        return;
      }

      // Step 1: Business Selection
      if (step === STEPS.SELECT_BUSINESSES) {
        if (this.selectedBusiness.length === 0) {
          this.validationMessage = 'Please select a business to continue';
          return;
        }
        this.step = STEPS.SELECT_TERMINALS;
        return;
      }

      // Step 2: Terminal Selection
      if (step === STEPS.SELECT_TERMINALS) {
        if (this.selectedTerminals.length === 0) {
          this.validationMessage = 'Please select locations';
          return;
        }
        this.step = STEPS.SELECT_REVENUE_GROUP;
        return;
      }

      // Step 3: Revenue Group Selection
      if (step === STEPS.SELECT_REVENUE_GROUP) {
        if (!this.revenueGroupId) {
          this.validationMessage = 'Please select revenue group';
          return;
        }

        if (this.revenueGroupId === REVENUE_GROUPS.FOOD) {
          this.step = STEPS.SELECT_FOODS;
        } else if (this.revenueGroupId === REVENUE_GROUPS.BEVERAGE) {
          this.step = STEPS.SELECT_BEVERAGES;
        } else {
          this.step = STEPS.SELECT_FOODS;
        }
        return;
      }

      // Step 4: Food Selection
      if (step === STEPS.SELECT_FOODS) {
        if (this.checkedFood.length === 0) {
          this.validationMessage = 'Please select foods';
          return;
        }
        this.step = STEPS.SELECT_BEVERAGES;
        return;
      }

      // Step 5: Beverage Selection
      if (step === STEPS.SELECT_BEVERAGES) {
        if (this.checkedBeverage.length === 0) {
          this.validationMessage = 'Please select beverages';
          return;
        }
      }
    },

    /**
     * Fetches product categories for the selected location
     * @param {string} locationId - The location ID to fetch categories for
     */
    async getCategories(locationId) {
      try {
        const res = await api.getOolioCategories({ locationId });

        if (res.data.code === 0) {
          this.categories = res.data.data;
        } else {
          utils.alert2('', 'Error fetching categories', 'error');
        }
      } catch (err) {
        utils.alert2('', 'Failed to load categories', 'error');
      }
    },

    /**
     * Fetches businesses and locations for the selected organization
     * @param {string} locationId - The organization ID to fetch businesses/locations for
     */
    async getTerminals(locationId) {
      try {
        const res = await api.getOolioLocations({ locationId });

        if (res.data.code === 0) {
          const businessData = res.data.data;

          // Store businesses for selection
          this.businessList = businessData;

          // Flatten all locations from all businesses into devices array
          this.devices = [];
          businessData.forEach(business => {
            if (business.locations && business.locations.length > 0) {
              business.locations.forEach(location => {
                // Add business reference to each location for filtering
                this.devices.push({
                  ...location,
                  businessId: business.id,
                  businessName: business.name
                });
              });
            }
          });
        } else {
          utils.alert2('', 'Error fetching businesses and locations', 'error');
        }
      } catch (err) {
        utils.alert2('', 'Failed to load businesses and locations', 'error');
      }
    },

    /**
     * Handles location selection and loads related data
     * @param {string} locationId - The selected location ID
     */
    async onSelectLocation(locationId) {
      this.isLoading = true;
      this.selectedLocation = locationId;
      this.validationMessage = '';

      await Promise.all([
        this.getCategories(locationId),
        this.getTerminals(locationId)
      ]);

      this.isLoading = false;
    },
  },
  /**
   * Initialize component by binding Oolio account
   * Fetches available organizations/locations using the OAuth code
   */
  async mounted() {
    if (!this.code) {
      utils.alert2('', 'Missing authorization code', 'error');
      return;
    }

    this.isLoading = true;

    try {
      const res = await api.oolioBind({ code: this.code });

      if (res.data.code === 0) {
        this.locationList = res.data.data;
      } else {
        utils.alert2('', 'Error binding location', 'error');
      }
    } catch (err) {
      const errorMessage = (err && err.response && err.response.data && err.response.data.message)
        ? err.response.data.message
        : 'Something went wrong, please contact administrator.';
      utils.alert2('', errorMessage, 'error');
    } finally {
      this.isLoading = false;
    }
  }
}
</script>


<style lang="scss">
  @import '../../../static/scss/oolio-configuration.scss';
</style>



// WEBPACK FOOTER //
// src/components/modal/oolioConfiguration.vue